import React, { lazy, Suspense, useEffect, useState } from 'react';
import SafeNavigation from '@/components/SafeNavigation';
import HeroSection from '@/components/HeroSection';
import PrivacySection from '@/components/PrivacySection';
import TrackingSection from '@/components/TrackingSection';
import ActionSection from '@/components/ActionSection';
import GetStartedSteps from '@/components/GetStartedSteps';
import Footer from '@/components/Footer';
import { SafeSEO } from '@/components/seo/SafeSEO';
import { SEO_ROUTES } from '@/seo/seo-routes';

// Lazy load below-the-fold sections
const LazyMonitoringSection = lazy(() => import('@/components/MonitoringSection'));
const LazyCredentialsSection = lazy(() => import('@/components/CredentialsSection'));
const LazyNotificationPreview = lazy(() => import('@/components/NotificationPreview'));
const LazyNewsletterSection = lazy(() => import('@/components/NewsletterSection'));

const MinimalIndex: React.FC = () => {
  const [showMonitoring, setShowMonitoring] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);

  useEffect(() => {
    const anchors = {
      monitoring: document.getElementById('monitoring-anchor'),
      credentials: document.getElementById('credentials-anchor'),
      notifications: document.getElementById('notifications-anchor'),
      newsletter: document.getElementById('newsletter-anchor')
    };
    
    const observers = {
      monitoring: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShowMonitoring(true);
            observers.monitoring.disconnect();
          }
        },
        { rootMargin: '400px' }
      ),
      credentials: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShowCredentials(true);
            observers.credentials.disconnect();
          }
        },
        { rootMargin: '400px' }
      ),
      notifications: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShowNotifications(true);
            observers.notifications.disconnect();
          }
        },
        { rootMargin: '400px' }
      ),
      newsletter: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShowNewsletter(true);
            observers.newsletter.disconnect();
          }
        },
        { rootMargin: '400px' }
      )
    };

    Object.entries(anchors).forEach(([key, anchor]) => {
      if (anchor) observers[key as keyof typeof observers].observe(anchor);
    });

    return () => {
      Object.values(observers).forEach(observer => observer.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen">
      <SafeSEO
        title="AI Smart Alerts for Parents"
        description="Keep your child safer on their iPhone with AI Smart Alerts. Spot bullying, risky contacts, money asks and safety risks across iMessage, Google, YouTube, WhatsApp and Snap - no spyware."
        url={SEO_ROUTES.index}
        image="/img/og/open_graph.png"
      />
      <SafeNavigation />
      <main>
        <HeroSection />
        <PrivacySection />
        <TrackingSection />
        <ActionSection />
        <div id="monitoring-anchor" />
        <Suspense fallback={null}>
          {showMonitoring && <LazyMonitoringSection />}
        </Suspense>
        <div id="credentials-anchor" />
        <Suspense fallback={null}>
          {showCredentials && <LazyCredentialsSection />}
        </Suspense>
        <div id="notifications-anchor" />
        <Suspense fallback={null}>
          {showNotifications && <LazyNotificationPreview />}
        </Suspense>
        <GetStartedSteps />
        <div id="newsletter-anchor" />
        <Suspense fallback={null}>
          {showNewsletter && <LazyNewsletterSection />}
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default MinimalIndex;

